import React, { memo } from 'react';
import { flexRender } from '@tanstack/react-table';
import type { HeaderGroup } from '@tanstack/react-table';
import { Checkbox } from './TableRow';
import type { DataGridColumn } from '../DataGrid.types';

export type DensityType = 'compact' | 'normal' | 'comfortable';

// Numeric cell renderer types for right-alignment
const NUMERIC_RENDERERS = ['currency', 'number', 'percent', 'priceChange'];

interface TableHeaderProps<T> {
  headerGroups: HeaderGroup<T>[];
  columns?: DataGridColumn<T>[];  // Original columns for checking cellRenderer
  enableSorting?: boolean;
  className?: string;
  enableSelection?: boolean;
  isAllSelected?: boolean;
  isPartiallySelected?: boolean;
  onToggleSelectAll?: () => void;
  // Column resize
  enableResize?: boolean;
  resizingColumnId?: string | null;
  getResizeHandler?: (columnId: string) => { onMouseDown: (e: React.MouseEvent) => void };
  getColumnWidth?: (columnId: string) => number;
  // Column reorder
  enableReorder?: boolean;
  draggedColumnId?: string | null;
  dropIndicatorIndex?: number | null;
  getDragHandlers?: (columnId: string) => {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragEnd: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  getColumnStyle?: (columnId: string) => React.CSSProperties;
  // Column pinning
  enablePinning?: boolean;
  isPinned?: (columnId: string) => 'left' | 'right' | null;
  onPinColumn?: (columnId: string, position: 'left' | 'right' | null) => void;
  // Density
  density?: DensityType;
}

// Density-based padding values
const DENSITY_PADDING = {
  compact: { vertical: '6px', horizontal: '10px' },
  normal: { vertical: '10px', horizontal: '14px' },
  comfortable: { vertical: '14px', horizontal: '16px' }
};

// Sort Icons - Compact design
const SortIcon = ({ direction }: { direction: 'asc' | 'desc' | false }) => {
  if (direction === 'asc') {
    return (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" style={{ opacity: 1 }}>
        <path d="M6 2l4 5H2l4-5z" />
      </svg>
    );
  }
  if (direction === 'desc') {
    return (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" style={{ opacity: 1 }}>
        <path d="M6 10L2 5h8l-4 5z" />
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" style={{ opacity: 0.25 }}>
      <path d="M6 2l3 3.5H3L6 2zM6 10L3 6.5h6L6 10z" />
    </svg>
  );
};

// Pin Icon - Compact
const PinIcon = ({ isPinned, position }: { isPinned: boolean; position?: 'left' | 'right' | null }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill={isPinned ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth="1.5"
    style={{
      opacity: isPinned ? 1 : 0.4,
      transform: position === 'right' ? 'scaleX(-1)' : 'none'
    }}
  >
    <path d="M6 1v3M3.5 4h5l-.8 3.2H4.3L3.5 4zM4.3 7.2v3M7.7 7.2v3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Drag Handle Icon - Compact
const DragHandleIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" style={{ opacity: 0.35 }}>
    <circle cx="3" cy="2.5" r="0.8" />
    <circle cx="7" cy="2.5" r="0.8" />
    <circle cx="3" cy="5" r="0.8" />
    <circle cx="7" cy="5" r="0.8" />
    <circle cx="3" cy="7.5" r="0.8" />
    <circle cx="7" cy="7.5" r="0.8" />
  </svg>
);

function TableHeaderComponent<T>({
  headerGroups,
  columns = [],
  enableSorting = true,
  className = '',
  enableSelection = false,
  isAllSelected = false,
  isPartiallySelected = false,
  onToggleSelectAll,
  enableResize = false,
  resizingColumnId,
  getResizeHandler,
  getColumnWidth,
  enableReorder = false,
  draggedColumnId,
  dropIndicatorIndex,
  getDragHandlers,
  getColumnStyle,
  enablePinning = false,
  isPinned,
  onPinColumn,
  density = 'normal'
}: TableHeaderProps<T>): React.ReactElement {
  const padding = DENSITY_PADDING[density];

  // Helper to check if column is numeric (for right-alignment)
  const isNumericColumn = (columnId: string): boolean => {
    const column = columns.find(c => c.id === columnId);
    return column ? NUMERIC_RENDERERS.includes(column.cellRenderer || '') : false;
  };

  return (
    <thead
      role="rowgroup"
      className={className}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 'var(--z-sticky)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)'
      } as React.CSSProperties}
    >
      {headerGroups.map(headerGroup => (
        <tr key={headerGroup.id} role="row" style={{ position: 'relative' }}>
          {/* Selection Checkbox Header */}
          {enableSelection && (
            <th
              role="columnheader"
              scope="col"
              aria-label={isAllSelected ? 'Tüm seçimleri kaldır' : 'Tümünü seç'}
              style={{
                padding: `${padding.vertical} ${padding.horizontal}`,
                background: 'var(--color-bg-header)',
                borderBottom: '1px solid var(--color-border)',
                borderRight: '1px solid var(--color-border-light)',
                width: '40px',
                minWidth: '40px',
                textAlign: 'left'
              }}
            >
              <Checkbox
                checked={isAllSelected}
                indeterminate={isPartiallySelected}
                onChange={() => onToggleSelectAll?.()}
              />
            </th>
          )}
          {headerGroup.headers.map((header, index) => {
            const canSort = enableSorting && header.column.getCanSort();
            const sortDirection = header.column.getIsSorted();
            const dragHandlers = enableReorder && getDragHandlers ? getDragHandlers(header.id) : {};
            const isDragging = draggedColumnId === header.id;
            const pinPosition = isPinned?.(header.id);
            const columnDragStyle = getColumnStyle?.(header.id) || {};

            return (
              <th
                key={header.id}
                data-column-index={index}
                role="columnheader"
                scope="col"
                aria-sort={
                  sortDirection === 'asc' ? 'ascending' :
                  sortDirection === 'desc' ? 'descending' :
                  canSort ? 'none' : undefined
                }
                tabIndex={canSort ? 0 : undefined}
                onClick={canSort && !enableReorder ? header.column.getToggleSortingHandler() : undefined}
                onKeyDown={(e) => {
                  // Handle Enter and Space for keyboard accessibility
                  if (canSort && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    header.column.getToggleSortingHandler()?.(e as unknown as React.MouseEvent);
                  }
                }}
                {...(enableReorder ? dragHandlers : {})}
                style={{
                  padding: `${padding.vertical} ${padding.horizontal}`,
                  textAlign: isNumericColumn(header.id) ? 'right' : 'left',
                  fontFamily: 'var(--font-body)',
                  fontSize: density === 'compact' ? '11px' : '12px',
                  fontWeight: 800,
                  letterSpacing: '0.3px',
                  textTransform: 'uppercase',
                  color: 'var(--color-text-secondary)',
                  background: pinPosition
                    ? 'rgba(var(--color-bg-header), 0.95)'
                    : resizingColumnId === header.id
                      ? 'var(--color-bg-hover)'
                      : 'var(--color-bg-header)',
                  cursor: enableReorder ? 'grab' : (canSort ? 'pointer' : 'default'),
                  userSelect: 'none',
                  position: 'relative',
                  width: getColumnWidth ? getColumnWidth(header.id) : header.getSize(),
                  minWidth: header.column.columnDef.minSize,
                  maxWidth: header.column.columnDef.maxSize,
                  borderBottom: '1px solid var(--color-border)',
                  borderRight: '1px solid var(--color-border-light)',
                  whiteSpace: 'nowrap',
                  // Drag styles from hook
                  ...columnDragStyle
                }}
                onMouseEnter={(e) => {
                  if (!isDragging && resizingColumnId !== header.id && !draggedColumnId) {
                    e.currentTarget.style.background = 'var(--color-bg-hover)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDragging && resizingColumnId !== header.id && !pinPosition && !draggedColumnId) {
                    e.currentTarget.style.background = 'var(--color-bg-header)';
                  }
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isNumericColumn(header.id) ? 'flex-end' : 'flex-start',
                    gap: density === 'compact' ? '4px' : '6px',
                    flex: 1
                  }}
                >
                  {/* Drag Handle */}
                  {enableReorder && (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'grab',
                        padding: '2px',
                        marginLeft: '-2px',
                        borderRadius: '2px',
                        color: 'var(--color-text-muted)'
                      }}
                    >
                      <DragHandleIcon />
                    </span>
                  )}
                  <span
                    style={{ flex: 1, cursor: canSort ? 'pointer' : 'default' }}
                    onClick={canSort && enableReorder ? header.column.getToggleSortingHandler() : undefined}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </span>
                  {canSort && (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: sortDirection ? 'var(--color-primary)' : 'var(--color-text-muted)',
                        cursor: 'pointer',
                        transition: 'color 0.15s ease'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        header.column.getToggleSortingHandler()?.(e);
                      }}
                    >
                      <SortIcon direction={sortDirection} />
                    </span>
                  )}
                  {/* Pin Button */}
                  {enablePinning && onPinColumn && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (pinPosition === 'left') {
                          onPinColumn(header.id, 'right');
                        } else if (pinPosition === 'right') {
                          onPinColumn(header.id, null);
                        } else {
                          onPinColumn(header.id, 'left');
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '2px',
                        border: 'none',
                        background: pinPosition ? 'var(--color-primary-100)' : 'transparent',
                        cursor: 'pointer',
                        color: pinPosition ? 'var(--color-primary)' : 'var(--color-text-muted)',
                        borderRadius: '3px',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!pinPosition) {
                          e.currentTarget.style.background = 'var(--color-bg-hover)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!pinPosition) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                      title={
                        pinPosition === 'left'
                          ? 'Sağa sabitle'
                          : pinPosition === 'right'
                            ? 'Sabitlemeyi kaldır'
                            : 'Sola sabitle'
                      }
                    >
                      <PinIcon isPinned={!!pinPosition} position={pinPosition} />
                    </button>
                  )}
                </div>
                {/* Drop Indicator - Left side - Enhanced visibility */}
                {enableReorder && dropIndicatorIndex === index && (
                  <div
                    style={{
                      position: 'absolute',
                      left: -2,
                      top: -2,
                      bottom: -2,
                      width: '4px',
                      background: 'var(--color-primary)',
                      borderRadius: '2px',
                      zIndex: 20,
                      boxShadow: '0 0 12px var(--color-primary), 0 0 4px var(--color-primary)',
                      animation: 'pulse 0.8s ease-in-out infinite'
                    }}
                  />
                )}
                {/* Drop Indicator - Right side (for last column) - Enhanced visibility */}
                {enableReorder && dropIndicatorIndex === headerGroup.headers.length && index === headerGroup.headers.length - 1 && (
                  <div
                    style={{
                      position: 'absolute',
                      right: -2,
                      top: -2,
                      bottom: -2,
                      width: '4px',
                      background: 'var(--color-primary)',
                      borderRadius: '2px',
                      zIndex: 20,
                      boxShadow: '0 0 12px var(--color-primary), 0 0 4px var(--color-primary)',
                      animation: 'pulse 0.8s ease-in-out infinite'
                    }}
                  />
                )}
                {/* Resize Handle */}
                {enableResize && getResizeHandler && (
                  <div
                    {...getResizeHandler(header.id)}
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '5px',
                      cursor: 'col-resize',
                      background: resizingColumnId === header.id
                        ? 'var(--color-primary)'
                        : 'transparent',
                      transition: 'background 0.15s ease',
                      zIndex: 1
                    }}
                    onMouseEnter={(e) => {
                      if (resizingColumnId !== header.id) {
                        e.currentTarget.style.background = 'var(--color-primary-300)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (resizingColumnId !== header.id) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
}

// Memoized export for performance
export const TableHeader = memo(TableHeaderComponent) as typeof TableHeaderComponent;
